import { useEffect, useSyncExternalStore } from "react";

export type WidgetPreferenceKey = "front" | "sentry";
export type WidgetPreferences = Record<WidgetPreferenceKey, boolean>;

const STORAGE_KEY = "supply-guard-widget-preferences";
const WIDGET_PREFERENCES_CHANGED_EVENT = "supply-guard-widget-preferences-changed";

export const DEFAULT_WIDGET_PREFERENCES: WidgetPreferences = {
  front: true,
  sentry: false,
};

let cachedWidgetPreferences: WidgetPreferences = DEFAULT_WIDGET_PREFERENCES;

const QUERY_PARAM_BY_WIDGET: Record<WidgetPreferenceKey, string> = {
  front: "supportChat",
  sentry: "bugReporting",
};

const LEGACY_QUERY_PARAM_BY_WIDGET: Record<WidgetPreferenceKey, string> = {
  front: "frontWidget",
  sentry: "sentryWidget",
};

function canUseBrowserStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function parseBooleanToggle(value: string | null): boolean | null {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (["1", "true", "on", "yes", "enabled"].includes(normalized)) {
    return true;
  }
  if (["0", "false", "off", "no", "disabled"].includes(normalized)) {
    return false;
  }
  return null;
}

function normalizePreferences(value: unknown): WidgetPreferences {
  if (!value || typeof value !== "object") {
    return DEFAULT_WIDGET_PREFERENCES;
  }

  const candidate = value as Partial<Record<WidgetPreferenceKey, unknown>>;
  return {
    front: typeof candidate.front === "boolean" ? candidate.front : DEFAULT_WIDGET_PREFERENCES.front,
    sentry: typeof candidate.sentry === "boolean" ? candidate.sentry : DEFAULT_WIDGET_PREFERENCES.sentry,
  };
}

function applySearchParamsToPreferences(preferences: WidgetPreferences, search: string): WidgetPreferences {
  const params = new URLSearchParams(search);
  let nextPreferences = preferences;

  (Object.keys(QUERY_PARAM_BY_WIDGET) as WidgetPreferenceKey[]).forEach((key) => {
    const parsed =
      parseBooleanToggle(params.get(QUERY_PARAM_BY_WIDGET[key])) ??
      parseBooleanToggle(params.get(LEGACY_QUERY_PARAM_BY_WIDGET[key]));
    if (parsed === null || nextPreferences[key] === parsed) {
      return;
    }

    nextPreferences = {
      ...nextPreferences,
      [key]: parsed,
    };
  });

  return nextPreferences;
}

function getStoredWidgetPreferences(): WidgetPreferences {
  if (!canUseBrowserStorage()) {
    return DEFAULT_WIDGET_PREFERENCES;
  }

  try {
    return normalizePreferences(JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "null"));
  } catch {
    return DEFAULT_WIDGET_PREFERENCES;
  }
}

function toStableWidgetPreferences(preferences: WidgetPreferences): WidgetPreferences {
  if (
    cachedWidgetPreferences.front === preferences.front &&
    cachedWidgetPreferences.sentry === preferences.sentry
  ) {
    return cachedWidgetPreferences;
  }

  cachedWidgetPreferences = preferences;
  return cachedWidgetPreferences;
}

function removeWidgetPreferenceQueryParam(key: WidgetPreferenceKey) {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  const paramName = QUERY_PARAM_BY_WIDGET[key];
  const legacyParamName = LEGACY_QUERY_PARAM_BY_WIDGET[key];
  if (!url.searchParams.has(paramName) && !url.searchParams.has(legacyParamName)) {
    return;
  }

  url.searchParams.delete(paramName);
  url.searchParams.delete(legacyParamName);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

export function getWidgetPreferences(): WidgetPreferences {
  if (!canUseBrowserStorage()) {
    return DEFAULT_WIDGET_PREFERENCES;
  }

  return toStableWidgetPreferences(applySearchParamsToPreferences(getStoredWidgetPreferences(), window.location.search));
}

export function setWidgetPreference(key: WidgetPreferenceKey, enabled: boolean): WidgetPreferences {
  removeWidgetPreferenceQueryParam(key);
  const storedPreferences = getStoredWidgetPreferences();
  const nextPreferences = {
    ...storedPreferences,
    [key]: enabled,
  };

  if (canUseBrowserStorage()) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextPreferences));
    window.dispatchEvent(new Event(WIDGET_PREFERENCES_CHANGED_EVENT));
  }

  return toStableWidgetPreferences(nextPreferences);
}

export function applyWidgetPreferenceDeeplinks(search: string): WidgetPreferences {
  const storedPreferences = getStoredWidgetPreferences();
  const nextPreferences = applySearchParamsToPreferences(storedPreferences, search);
  const didUpdate = nextPreferences.front !== storedPreferences.front || nextPreferences.sentry !== storedPreferences.sentry;

  if (didUpdate && canUseBrowserStorage()) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextPreferences));
    window.dispatchEvent(new Event(WIDGET_PREFERENCES_CHANGED_EVENT));
  }

  return toStableWidgetPreferences(nextPreferences);
}

function subscribe(callback: () => void) {
  if (typeof window === "undefined") {
    return () => {};
  }

  window.addEventListener(WIDGET_PREFERENCES_CHANGED_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(WIDGET_PREFERENCES_CHANGED_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}

export function useWidgetPreferences() {
  const preferences = useSyncExternalStore(subscribe, getWidgetPreferences, () => DEFAULT_WIDGET_PREFERENCES);

  return {
    preferences,
    setPreference: setWidgetPreference,
  };
}

export function useWidgetPreferenceDeeplinks(search: string) {
  useEffect(() => {
    applyWidgetPreferenceDeeplinks(search);
  }, [search]);
}
